import clsx from "clsx";

/** Small right-pointing arrow used by the Select menu cells. */
export function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      width="17"
      height="14"
      viewBox="0 0 17 14"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M10 1L16 7L10 13M1 7H16"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Shared "Select menu" cell — the Figma atom used by the header Products/Services
 * dropdowns and by the in-page feature tab lists (e.g. HackGrid product). Per the
 * design the active cell turns primary/red (text + left border) over a subtle
 * gradient. Text size stays fixed at body-1-heavy (16px) — we deliberately do NOT
 * grow it to Figma's 20px so it doesn't jump on hover.
 *
 * Three knobs let the header and the in-page lists differ:
 * - `showArrow` (default true): the header keeps the trailing arrow; in-page
 *   feature tabs hide it.
 * - `alwaysGradient` (default false): the header cell is transparent when idle
 *   and only reveals the gradient on hover/active; in-page feature tabs show the
 *   gradient as their resting state.
 * - `boldWhenActive` (default false): in-page feature tabs also carry the
 *   selection in weight, not colour alone. The header opts out — there `active`
 *   is hover-preview state, so the label would thicken under the pointer.
 *
 * This cell is `lg`+ only at every call site: the header dropdowns do not exist
 * on the mobile menu, and HackGrid's feature list swaps it for the shared mobile
 * accordion below `lg` (see {@link ./accordion}). So it never needs a collapsed
 * or disclosure variant — nothing here carries a sub-`lg` breakpoint.
 *
 * `active` is transient pointer state ("the cell being previewed"), while
 * `current` means "this cell IS the page you are on". They are separate because
 * hovering another cell must not erase where you are: a current-but-not-previewed
 * cell keeps the red text + left border, just without the gradient.
 */
export function SelectCell({
  label,
  activeLabel,
  active,
  current = false,
  onMouseEnter,
  onClick,
  showArrow = true,
  alwaysGradient = false,
  boldWhenActive = false,
  className,
}: {
  label: string;
  activeLabel?: string;
  active: boolean;
  /** Marks the cell as the current page (adds `aria-current="page"`). */
  current?: boolean;
  onMouseEnter?: () => void;
  onClick?: () => void;
  showArrow?: boolean;
  alwaysGradient?: boolean;
  /** Lifts the highlighted label from Demi (600) to Bold (700). */
  boldWhenActive?: boolean;
  /** Extra classes for the button, e.g. to let it grow to fill a column. */
  className?: string;
}) {
  const highlighted = active || current;
  // In the header the cell sits on a uniform dropdown surface, so the gradient
  // can fade to transparent. In-page (alwaysGradient) it sits on the decorative
  // patterned page background — fading to transparent would let that pattern show
  // through and read as a second background layer, so it fades to the OPAQUE base
  // colour (#15181d) instead, keeping the cell a single clean surface.
  const gradient = alwaysGradient
    ? "bg-gradient-to-r from-[#2e353a] to-[#15181d]"
    : "bg-gradient-to-r from-[#2e353a] to-[rgba(21,24,29,0)]";
  return (
    <button
      type="button"
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      aria-pressed={active}
      aria-current={current ? "page" : undefined}
      className={clsx(
        // 36px side padding eats a tenth of a phone screen: below `sm` the cell
        // pulls in to 20px so the label keeps its single line inside the
        // in-page feature lists (the header dropdowns are `lg`+ only).
        "group flex h-16 w-full items-center justify-between px-5 py-3 sm:px-9 sm:py-4 transition-all duration-300 cursor-pointer border-l-[5px]",
        className,
        active
          ? `${gradient} border-primary`
          : current
            ? "border-primary"
            : alwaysGradient
              ? `${gradient} border-transparent hover:from-[#3a424a] hover:border-primary`
              : "border-transparent hover:bg-gradient-to-r hover:from-[#2e353a] hover:to-[rgba(21,24,29,0)] hover:border-primary",
      )}
    >
      <span
        className={clsx(
          "body-1-heavy text-left transition-colors duration-300 whitespace-nowrap",
          highlighted ? "text-primary" : "text-text-default",
          // `body-1-heavy` is a custom utility that pins weight 600, so it sits
          // in the same layer as `font-bold` and a plain one would not reliably
          // win. `!` is the idiom the rest of the site uses to lift a
          // body-1-heavy label to Bold (cf. ISOStandard.tsx).
          highlighted && boldWhenActive && "font-bold!",
        )}
      >
        {highlighted && activeLabel ? activeLabel : label}
      </span>
      {showArrow && (
        <ArrowIcon
          className={clsx(
            "flex-shrink-0 ml-6 transition-colors duration-300",
            highlighted ? "text-primary" : "text-text-default",
          )}
        />
      )}
    </button>
  );
}
